// components/Atoms/Card/Card.js
import React from 'react';
import PropTypes from 'prop-types';

/**
 * Card - Universal card container
 * 
 * A card is a surface component that:
 * - Represents a domain entity (Article, User, Product, etc.)
 * - Works in any context (grid, list, carousel, modal)
 * - Extends .card base with surface/padding/hover variants
 * 
 * Semantic usage (recommended):
 *   <Card usage="elevated">{children}</Card>
 *   <Card usage="interactive">{children}</Card>
 * 
 * Manual usage (edge cases):
 *   <Card surface="outlined" padding="normal">{children}</Card>
 */

// Semantic usage presets
const USAGE_PRESETS = {
  elevated: { surface: 'elevated', padding: 'normal', hover: 'lift' },
  flat: { surface: 'flat', padding: 'normal', hover: 'none' },
  outlined: { surface: 'outlined', padding: 'normal', hover: 'border' },
  interactive: { surface: 'elevated', padding: 'normal', hover: 'scale' },
};

const Card = ({ 
  usage,                    // Semantic preset (recommended)
  surface,                  // Surface style: flat, elevated, outlined (NO DEFAULT)
  padding = 'normal',       // Padding: none, tight, normal, loose
  hover = 'none',           // Hover effect: none, lift, scale, border
  className = '',
  children,
  ...props 
}) => {
  // Apply usage preset if provided
  const finalSurface = usage && USAGE_PRESETS[usage] 
    ? USAGE_PRESETS[usage].surface 
    : surface;
    
  const finalPadding = usage && USAGE_PRESETS[usage]
    ? USAGE_PRESETS[usage].padding
    : padding;
    
  const finalHover = usage && USAGE_PRESETS[usage]
    ? USAGE_PRESETS[usage].hover
    : hover;
  
  // Build CSS classes - Only add surface if specified
  const surfaceClass = finalSurface ? {
    flat: 'surface-flat',
    elevated: 'surface-elevated',
    outlined: 'surface-outlined',
  }[finalSurface] : '';
  
  const paddingClass = {
    none: 'surface-pad-none',
    tight: 'surface-pad-tight',
    normal: 'surface-pad-normal',
    loose: 'surface-pad-loose',
  }[finalPadding];
  
  const hoverClass = {
    none: '',
    lift: 'surface-hover-lift',
    scale: 'surface-hover-scale',
    border: 'surface-hover-border',
  }[finalHover];
  
  const cardClasses = [
    'card',
    surfaceClass,
    paddingClass,
    hoverClass,
    className,
  ].filter(Boolean).join(' ');
  
  return (
    <div className={cardClasses} {...props}>
      {children}
    </div>
  );
};

Card.propTypes = {
  // Semantic usage (recommended)
  usage: PropTypes.oneOf(['elevated', 'flat', 'outlined', 'interactive']),
  
  // Manual control (edge cases)
  surface: PropTypes.oneOf(['flat', 'elevated', 'outlined']),
  padding: PropTypes.oneOf(['none', 'tight', 'normal', 'loose']),
  hover: PropTypes.oneOf(['none', 'lift', 'scale', 'border']),
  
  // Standard props
  className: PropTypes.string,
  children: PropTypes.node.isRequired,
};

export default Card;